import React, { useState } from 'react';
import { AgentVersion, ModelsConfig } from '@/redux/internal-services/agentsApi';
import { AgentFormValues, Modal, relTime, promptText, lineDiff, labelCls, inputCls } from './agentShared';

// ── VersionsTab ────────────────────────────────────────────────────────────────

interface VersionsTabProps {
  versions: AgentVersion[];
  publishedId?: string;
  dirty: boolean;
  latestN?: number;
  onSaveVersion: () => void;
  onView: (v: AgentVersion) => void;
}

export const VersionsTab: React.FC<VersionsTabProps> = ({
  versions,
  publishedId,
  dirty,
  latestN,
  onSaveVersion,
  onView,
}) => (
  <div>
    <div className="mb-3 flex items-center justify-between">
      <p className="text-sm text-ui-text2">
        Each version is a snapshot of the prompt, model, parameters, and tools. Publish one to mark it live.
      </p>
      <button
        type="button"
        onClick={onSaveVersion}
        className="bg-brand-dark text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-brand-dark/90"
      >
        Save version
      </button>
    </div>

    <div
      className={`mb-5 flex items-center gap-3 rounded-xl border p-4 ${
        dirty ? 'border-amber-200 bg-amber-50' : 'border-[#EDEDED] dark:border-white/10 bg-ui-card'
      }`}
    >
      <div className="flex-1">
        <div className="text-sm font-medium text-ui-text">Working draft</div>
        <div className="text-xs text-ui-text2">
          {dirty
            ? latestN
              ? `Unsaved changes since v${latestN}`
              : 'Not yet versioned'
            : `Matches v${latestN}`}
        </div>
      </div>
      {dirty && (
        <button
          type="button"
          onClick={onSaveVersion}
          className="border border-[#EDEDED] dark:border-white/10 text-ui-text px-3 py-1.5 rounded-lg text-sm hover:bg-ui-hover"
        >
          Save version
        </button>
      )}
    </div>

    {versions.length === 0 && <p className="text-xs text-ui-text3">No versions saved yet.</p>}

    <div className="space-y-2">
      {versions.map((v) => (
        <div key={v.id} className="flex items-center gap-3 rounded-xl border border-[#EDEDED] dark:border-white/10 bg-ui-card px-4 py-3">
          <span className="font-mono rounded-md bg-ui-subtle px-2 py-0.5 text-xs font-medium text-ui-text">
            v{v.n}
          </span>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm text-ui-text">{v.message}</div>
            <div className="text-xs text-ui-text3">{relTime(v.created_at)}</div>
          </div>
          {v.id === publishedId && (
            <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
              ● Live
            </span>
          )}
          <button
            type="button"
            onClick={() => onView(v)}
            className="border border-[#EDEDED] dark:border-white/10 text-ui-text px-3 py-1.5 rounded-lg text-sm hover:bg-ui-hover"
          >
            View
          </button>
        </div>
      ))}
    </div>
  </div>
);

// ── SaveVersionModal ───────────────────────────────────────────────────────────

interface SaveVersionModalProps {
  nextN: number;
  onClose: () => void;
  onSave: (message: string, publish: boolean) => void;
}

export const SaveVersionModal: React.FC<SaveVersionModalProps> = ({ nextN, onClose, onSave }) => {
  const [message, setMessage] = useState('');
  const [publish, setPublish] = useState(true);
  return (
    <Modal
      title={`Save version ${nextN}`}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="border border-[#EDEDED] dark:border-white/10 text-ui-text px-3 py-1.5 rounded-lg text-sm hover:bg-ui-hover"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSave(message.trim() || `Version ${nextN}`, publish)}
            className="bg-brand-dark text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-brand-dark/90"
          >
            Save version
          </button>
        </>
      }
    >
      <div className="mb-4">
        <label className={labelCls}>
          What changed? <span className="text-ui-text3 font-normal">— shows in history</span>
        </label>
        <input
          autoFocus
          className={inputCls}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="e.g. Tightened refund policy, added lookup tool"
        />
      </div>
      <label className="flex cursor-pointer items-center gap-2.5 rounded-lg border border-[#EDEDED] dark:border-white/10 px-3 py-2.5 text-sm">
        <input
          type="checkbox"
          checked={publish}
          onChange={(e) => setPublish(e.target.checked)}
          className="h-4 w-4 accent-brand-dark"
        />
        Publish this version (mark it live)
      </label>
      <p className="mt-2 text-xs text-ui-text2">Snapshots the current draft and saves the agent.</p>
    </Modal>
  );
};

// ── ViewVersionModal ───────────────────────────────────────────────────────────

interface ViewVersionModalProps {
  version: AgentVersion;
  current: AgentFormValues;
  isPublished: boolean;
  onClose: () => void;
  onRestore: () => void;
  onPublish: () => void;
}

export const ViewVersionModal: React.FC<ViewVersionModalProps> = ({
  version,
  current,
  isPublished,
  onClose,
  onRestore,
  onPublish,
}) => {
  const snap = version.snapshot as Partial<AgentFormValues>;
  const diff = lineDiff(
    promptText(snap.system_prompts as Array<{ title?: string; prompt: string }>),
    promptText(current.system_prompts),
  );
  const changed = diff.some((l) => l.t !== 'same');

  return (
    <Modal
      wide
      title={`Version ${version.n}`}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="border border-[#EDEDED] dark:border-white/10 text-ui-text px-3 py-1.5 rounded-lg text-sm hover:bg-ui-hover"
          >
            Close
          </button>
          {!isPublished && (
            <button
              type="button"
              onClick={onPublish}
              className="border border-[#EDEDED] dark:border-white/10 text-ui-text px-3 py-1.5 rounded-lg text-sm hover:bg-ui-hover"
            >
              Publish to live
            </button>
          )}
          <button
            type="button"
            onClick={onRestore}
            className="bg-brand-dark text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-brand-dark/90"
          >
            Restore to draft
          </button>
        </>
      }
    >
      <div className="mb-3 flex items-center gap-2 text-sm">
        <span className="font-mono rounded bg-ui-subtle px-2 py-0.5 text-xs">v{version.n}</span>
        <span className="text-ui-text">{version.message}</span>
        {isPublished && (
          <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
            ● Live
          </span>
        )}
        <span className="flex-1" />
        <span className="text-xs text-ui-text3">{relTime(version.created_at)}</span>
      </div>

      <div className="mb-1 text-xs font-medium uppercase tracking-wide text-ui-text3">Model</div>
      <p className="mb-3 text-sm font-mono">
        {String((snap.models as ModelsConfig)?.text)}
        {(snap.models as ModelsConfig)?.text !== (current.models as ModelsConfig)?.text && (
          <span className="text-emerald-700"> → {(current.models as ModelsConfig)?.text} (draft)</span>
        )}
      </p>

      <div className="mb-1 text-xs font-medium uppercase tracking-wide text-ui-text3">
        Prompt — this version → current draft
      </div>
      {changed ? (
        <div className="font-mono max-h-72 overflow-y-auto rounded bg-ui-raised border border-[#EDEDED] dark:border-white/10 p-2 text-[12px] leading-relaxed">
          {diff.map((l, i) => (
            <div
              key={i}
              className={
                l.t === 'add'
                  ? 'bg-emerald-50 text-emerald-800'
                  : l.t === 'del'
                  ? 'bg-red-50 text-red-700 line-through'
                  : 'text-ui-text2'
              }
            >
              <span className="select-none pr-1 text-ui-text3">
                {l.t === 'add' ? '+' : l.t === 'del' ? '−' : ' '}
              </span>
              {l.v || ' '}
            </div>
          ))}
        </div>
      ) : (
        <p className="rounded-lg border border-[#EDEDED] dark:border-white/10 bg-ui-card p-3 text-sm text-ui-text3">
          Prompt matches the current draft.
        </p>
      )}
    </Modal>
  );
};
